// common
import React from 'react';
import { useLocation } from 'react-router-dom';

import { TermsModal } from 'contents/modal/TermsModal';
// style
import styled, { css } from 'styled-components';

import MemoizedButtonGroup from 'components/button/ButtonGroup';
import DropDown from 'components/form/dropdown/DropDown';
import FooterLogo from 'components/logo/FooterLogo';

import useViewMode from 'hooks/common/useViewMode';
import useModal from 'hooks/components/useModal';

import { clickToGTM } from 'utils/seo/seoGTMUtil';

import { footerConst } from 'constants/common/footer/FooterConst';

import { typography } from 'styles/token/typography';

// components
import { CalcFooter } from './CalcFooter';

const Footer = () => {
  const location = useLocation();
  const { visibleModal, openModal, closeModal } = useModal();
  const { isMobile, openDesktopView, openMobileView } = useViewMode();

  const handleFamilyClick = (option) => {
    const clickItem = footerConst.familySite[option.idx];
    clickToGTM({
      target: clickItem.title,
      id: clickItem.type,
      url: clickItem.url,
    });
    window.open(clickItem.url, '_blank', 'noopener,noreferrer');
  };

  const renderFamilyDropDown = () => (
    <FamilySiteDropDown
      label="FAMILY SITE"
      list={footerConst.familySite.map((item, idx) => ({
        idx,
        value: item.title,
        type: item.type,
        dataType: item.type,
      }))}
      onDropDownClick={handleFamilyClick}
    />
  );

  const renderSocialIcons = (isWeb = false) =>
    footerConst.social.map((element, idx) =>
      isWeb ? (
        <SocialText
          key={idx}
          onClick={() => {
            clickToGTM({
              target: element.title,
              id: element.type,
              url: element.url,
            });
            window.open(element.url, '_blank', 'noopener,noreferrer');
          }}
        >
          <SocialLogo color={element.color} $isMobile={isMobile}>
            {element.icon.web}
          </SocialLogo>
          {element.title}
        </SocialText>
      ) : (
        <SocialLogo
          $isMobile={isMobile}
          key={idx}
          onClick={() => {
            clickToGTM({
              target: element.title,
              id: element.type,
              url: element.url,
            });
            window.open(element.url, '_blank', 'noopener,noreferrer');
          }}
        >
          {element.icon.mobile}
        </SocialLogo>
      )
    );

  const FooterContent = () => (
    <FooterContainer className="footer_container">
      <FooterTitle className="inavi_footer">
        (주)아이나비시스템즈 <span>(INAVI Maps)</span>
      </FooterTitle>
      <FooterTerms>
        <MemoizedButtonGroup
          button={[
            {
              text: isMobile ? 'PC 버전으로 보기' : '모바일 버전으로 보기',
              onClick: () => (isMobile ? openDesktopView() : openMobileView()),
            },
            {
              text: '개인정보처리방침',
              onClick: () =>
                openModal(
                  'partnershipTerms',
                  '㈜ 아이나비시스템즈 개인(위치)정보 처리방침'
                ),
            },
          ]}
        />
      </FooterTerms>
      <FooterInfo className="footer_info">
        <FooterInfoGroup>
          <Text color="black" className={footerConst.info[0].className}>
            {footerConst.info[0].title} : {footerConst.info[0].content}
          </Text>
          <FooterInfoInnerBox>
            {footerConst.info.slice(1).map((element, idx) => (
              <Text color="black" key={idx} className={element.className}>
                {element.title} : {element.content}
              </Text>
            ))}
          </FooterInfoInnerBox>
        </FooterInfoGroup>
      </FooterInfo>
      <CopyText className="copy-text">
        © iNavi Systems Corp. All rights reserved.
      </CopyText>
    </FooterContainer>
  );

  return (
    <>
      {location.pathname.includes('/billing-plan-calc') && !isMobile && (
        <CalcFooter />
      )}
      <FooterStyle id="footer">
        <FooterInner className="footer_inner">
          <ContentInner className="content_box">
            <FooterHeader $isMobile={isMobile}>
              <FooterLogo />
              {isMobile && renderFamilyDropDown()}
            </FooterHeader>
            <FooterContent />
          </ContentInner>

          {/* 소셜 영역 */}
          <SocialInner className="social_box">
            {isMobile ? (
              <FamilySite $isMobile={isMobile}>
                {renderSocialIcons()}
              </FamilySite>
            ) : (
              <>
                <FamilySite>{renderFamilyDropDown()}</FamilySite>
                <SocialGroup>
                  <SocialDesc>
                    INAVI Maps의 SNS 채널을 구독하시고, 소식도 빠르게 확인해
                    보세요.
                  </SocialDesc>
                  <SocialList className="social_info">
                    {renderSocialIcons(true)}
                  </SocialList>
                </SocialGroup>
              </>
            )}
          </SocialInner>
        </FooterInner>
      </FooterStyle>

      {/* 이용 약관 모달 */}
      {visibleModal.modalType === 'partnershipTerms' && (
        <TermsModal visibleModal={visibleModal} closeModal={closeModal} />
      )}
    </>
  );
};

export default Footer;

// footer 최상위 스타일
const FooterStyle = styled.footer`
  margin-top: auto;
  text-align: left;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.gray100};
`;

const FooterInner = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  // max-width: 1280px;
  max-width: 1440px;
  margin: 0 auto;
  padding: 32px 45px;

  @media screen and (max-width: 1025px) {
    display: inline-block;
    padding: 24px;
  }
`;
const ContentInner = styled.div`
  flex: 0 0 50%;
`;
const SocialInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 0 0 50%;

  @media screen and (max-width: 1025px) {
    flex-direction: row;
    align-items: end;
    margin-top: 16px;
    gap: 8px;
  }
`;

const FooterHeader = styled.div`
  display: flex;
  align-items: flex-start;
  height: 35px;
  gap: ${({ $isMobile }) => ($isMobile ? '24px' : '0')};

  @media screen and (max-width: 1025px) {
    flex-direction: column;
    gap: 8px;
  }
`;
const FamilySite = styled.div`
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      display: flex;
      align-items: center;
      gap: 20px;
    `}
`;
const SocialGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const SocialDesc = styled.div`
  ${typography.labelLarge}
  font-weight: 400;
`;

const SocialList = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  @media screen and (max-width: 1025px) {
    justify-content: flex-start;
  }
`;

const SocialText = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: ${({ theme }) => theme.color.gray800};
  ${typography.labelLarge}
  font-weight: 400;
  cursor: pointer;
`;
const FooterTitle = styled.p`
  color: ${({ theme }) => theme.color.gray900};
  ${typography.labelLarge}

  span {
    color: ${({ theme }) => theme.color.gray500};
    font-weight: 400;
  }

  @media screen and (max-width: 768px) {
    font-size: 13px;
  }
`;

// footer 컨테이너 스타일
const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;

  @media screen and (max-width: 1025px) {
    margin-top: 50px;
  }
`;
const FooterTerms = styled.div`
  > div {
    gap: 16px;
  }

  button {
    ${typography.labelLarge}
    font-weight: 300;

    &:hover {
      color: ${({ theme }) => theme.color.primary};
    }
  }
`;
const FooterInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  color: ${({ theme }) => theme.color.gray600};
  gap: 6px;
`;
const FooterInfoGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 600px;
  gap: 4px;

  @media screen and (max-width: 768px) {
    margin-top: 5px;
    gap: 2px;
  }
`;

const FooterInfoInnerBox = styled.div`
  display: flex;
`;

const CopyText = styled.p`
  ${typography.bodyExtraSmall}
  color: ${({ theme }) => theme.color.gray500};

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`;

const Text = styled.p`
  ${typography.labelLarge}
  font-weight: 300;

  span,
  a {
    color: ${({ theme, color }) =>
      color === 'black' ? theme.color.gray900 : theme.color.gray500};
  }

  span,
  a {
    color: ${({ theme }) => theme.color.gray500};
    ${typography.labelLarge}
    font-weight: 300;
  }

  &:after {
    display: inline-block;
    margin: 0 10px;
    content: ' ';
    height: 10px;
    border-left: 1px solid ${({ theme }) => theme.color.gray300};
  }

  &:last-child {
    &:after {
      display: none;
    }
  }

  &.underline {
    text-decoration: underline;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &.address {
    &:after {
      display: none;
    }
  }

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`;

const FamilySiteDropDown = styled(DropDown)`
  width: max-content;
  label {
    width: max-content;
    font-size: 13px;
    gap: 24px;

    @media screen and (max-width: 768px) {
      font-size: 12px;
    }
  }
  ul {
    top: auto;
    bottom: 100%;
    margin-bottom: -4px;
    border-radius: 6px 6px 0 0;
    li {
      font-size: 13px;

      @media screen and (max-width: 768px) {
        font-size: 12px;
      }
    }
  }
`;

const SocialLogo = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${({ color }) => (color !== 'transparent' ? '50%' : '0')};
  padding: ${({ color }) => (color === '#ffffff' ? '2px' : '0')};
  background-color: ${({ color }) => color || 'transparent'};
  border: 1px solid
    ${({ color, theme }) =>
      color === '#ffffff' ? theme.color.gray200 : color || 'transparent'};
  overflow: hidden;

  &:hover {
    color: ${({ theme }) => theme.color.primary};
  }

  svg,
  img {
    width: 100%;
    height: 100%;
  }
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      svg {
        fill: ${({ theme }) => theme.color.gray400};
        circle {
          fill: ${({ theme }) => theme.color.gray400};
        }
      }
    `}

  ${({ color }) =>
    color && color !== '#ffffff' && color !== 'transparent'
      ? `
      svg, img {
        width: 60%;
        height: 60%;
        filter: brightness(0) invert(1);
      }
      `
      : ''}
  @media screen and (max-width: 1025px) {
    background-color: transparent;
    border: none;
    svg,
    img {
      width: 100%;
      height: 100%;
      filter: none;
    }
    svg {
      fill: ${({ theme }) => theme.color.gray400};
      circle {
        fill: ${({ theme }) => theme.color.gray400};
      }
    }
  }
`;
